import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Users, Plus, AlertCircle } from 'lucide-react';
import { LogoLoader } from '@/components/ui/logo-loader';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { useAlert } from '@/hooks/use-alert';
import { useQueryClient } from '@tanstack/react-query';

interface SeatUpgradePromptProps {
  orgId: string;
  currentSeats: number;
  maxTeamMembers: number;
  activeMembers: number;
  pricePerSeat?: number;
  currency?: string;
  onSuccess?: (result: SeatChangeResult) => void | Promise<void>;
  variant?: 'banner' | 'inline' | 'dialog';
  showTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface SeatChangeResult {
  success: boolean;
  newSeatCount: number;
  stripeUpdated: boolean;
  prorationApplied: string;
  effectiveLimits: {
    maxProjects: number | null;
    maxPdfPlans: number | null;
    maxTeamMembers: number | null;
    storageMb: number | null;
  };
  message?: string;
  error?: string;
}

export function SeatUpgradePrompt({
  orgId,
  currentSeats,
  maxTeamMembers,
  activeMembers,
  pricePerSeat = 29,
  currency = 'EUR',
  onSuccess,
  variant = 'banner',
  showTrigger = true,
  open,
  onOpenChange,
}: SeatUpgradePromptProps) {
  const { t } = useTranslation(['pages.settings', 'common']);
  const { toast } = useToast();
  const { confirm } = useAlert();
  const queryClient = useQueryClient();
  const [internalShowDialog, setInternalShowDialog] = useState(false);
  const [seatsToAdd, setSeatsToAdd] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Support both controlled and uncontrolled dialog state
  const isControlled = open !== undefined;
  const showDialog = isControlled ? open : internalShowDialog;
  const setShowDialog = isControlled ? (onOpenChange || (() => {})) : setInternalShowDialog;

  const hasReachedLimit = activeMembers >= maxTeamMembers;
  
  // When externally controlled (open prop provided), always render the dialog
  // When using showTrigger, only render if limit is reached
  if (!isControlled && !hasReachedLimit && showTrigger) {
    return null;
  }

  const calculateNewLimits = (additionalSeats: number) => {
    const newTotalSeats = currentSeats + additionalSeats;
    const multiplier = currentSeats > 0 ? newTotalSeats / currentSeats : 1;
    return {
      seats: newTotalSeats,
      members: Math.round(maxTeamMembers * multiplier),
    };
  };

  const handleAddSeats = async () => {
    if (seatsToAdd <= 0) {
      setError(t('pages.settings:sections.members.seatUpgrade.invalidCount'));
      return;
    }

    const totalAmount = formatCurrency(pricePerSeat * seatsToAdd, currency);
    const confirmed = await confirm({
      title: t('pages.settings:sections.members.seatUpgrade.confirmPurchaseTitle'),
      message: t('pages.settings:sections.members.seatUpgrade.confirmPurchaseMessage', {
        seats: seatsToAdd,
        amount: totalAmount,
      }),
      confirmText: t('pages.settings:sections.members.seatUpgrade.confirmPurchaseButton'),
      cancelText: t('common:alerts.cancel'),
      variant: 'default',
    });

    if (!confirmed) {
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      const response = await apiRequest(`/api/subscriptions/organization/${orgId}/seats/add`, {
        method: 'POST',
        body: JSON.stringify({ count: seatsToAdd }),
      });

      const result: SeatChangeResult = await response.json();

      if (result.success) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['/api/subscriptions/organization', orgId] }),
          queryClient.invalidateQueries({ queryKey: ['/api/subscriptions/organization', orgId, 'usage'] }),
          queryClient.invalidateQueries({ queryKey: ['/api/orgs/me'] }),
          queryClient.invalidateQueries({ queryKey: ['/api/orgs', orgId, 'members'] }),
          queryClient.invalidateQueries({ queryKey: ['/api/orgs', orgId, 'invitations'] }),
        ]);

        toast({
          title: t('pages.settings:sections.members.seatUpgrade.successTitle'),
          description: t('pages.settings:sections.members.seatUpgrade.successDescription', {
            added: seatsToAdd,
            total: result.newSeatCount,
          }),
        });

        setShowDialog(false);
        setSeatsToAdd(1);
        await onSuccess?.(result);
      } else {
        setError(result.error || t('pages.settings:sections.members.seatUpgrade.error'));
      }
    } catch (err: any) {
      setError(err.message || t('pages.settings:sections.members.seatUpgrade.error'));
    } finally {
      setIsAdding(false);
    }
  };

  const formatCurrency = (amount: number, curr: string) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: curr,
    }).format(amount);
  };

  const renderBanner = () => (
    <Alert className="mb-4 border-amber-500 bg-amber-50">
      <AlertCircle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-amber-900">
        {t('pages.settings:sections.members.seatUpgrade.title')}
      </AlertTitle>
      <AlertDescription className="text-amber-800 mt-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            {t('pages.settings:sections.members.seatUpgrade.message', {
              used: activeMembers,
              total: maxTeamMembers,
            })}
          </div>
          <Button
            onClick={() => setShowDialog(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('pages.settings:sections.members.seatUpgrade.addSeats')}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );

  const renderInline = () => (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <Users className="h-5 w-5 text-amber-600 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-medium text-amber-900">
            {t('pages.settings:sections.members.seatUpgrade.title')}
          </h4>
          <p className="text-sm text-amber-800 mt-1">
            {t('pages.settings:sections.members.seatUpgrade.message', {
              used: activeMembers,
              total: maxTeamMembers,
            })}
          </p>
          <Button
            onClick={() => setShowDialog(true)}
            variant="outline"
            size="sm"
            className="mt-3 border-amber-600 text-amber-700 hover:bg-amber-100"
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('pages.settings:sections.members.seatUpgrade.addSeats')}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {variant === 'banner' && renderBanner()}
      {variant === 'inline' && renderInline()}

      {/* Add Seats Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('pages.settings:sections.members.seatUpgrade.dialogTitle')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Current Usage */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-slate-600">
                  {t('pages.settings:sections.members.seatUpgrade.currentUsage')}
                </span>
                <span className="text-sm font-medium">
                  {activeMembers} / {maxTeamMembers}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-amber-500 h-2 rounded-full"
                  style={{ width: `${Math.min((activeMembers / maxTeamMembers) * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Seat Selector */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                {t('pages.settings:sections.members.seatUpgrade.seatsToAdd')}
              </label>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSeatsToAdd(Math.max(1, seatsToAdd - 1))}
                  disabled={seatsToAdd <= 1}
                  className="w-10 h-10 p-0"
                >
                  −
                </Button>
                <Input
                  type="number"
                  min="1"
                  value={seatsToAdd}
                  onChange={(e) => setSeatsToAdd(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSeatsToAdd(seatsToAdd + 1)}
                  className="w-10 h-10 p-0"
                >
                  +
                </Button>
              </div>
            </div>

            {/* Price Info */}
            <div className="bg-blue-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-700">
                  {t('pages.settings:sections.members.seatUpgrade.pricePerSeat')}
                </span>
                <span className="font-medium">
                  {formatCurrency(pricePerSeat, currency)}
                </span>
              </div>
              <div className="flex justify-between items-center text-lg font-semibold border-t border-blue-200 pt-2">
                <span>{t('pages.settings:sections.members.seatUpgrade.total')}</span>
                <span>{formatCurrency(pricePerSeat * seatsToAdd, currency)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {t('pages.settings:sections.members.seatUpgrade.proratedNote')}
              </p>
            </div>

            {/* New Limits Preview */}
            {seatsToAdd > 0 && (
              <div className="bg-green-50 p-3 rounded-lg">
                <h5 className="text-sm font-medium text-green-900 mb-2">
                  {t('pages.settings:sections.members.seatUpgrade.newLimits')}
                </h5>
                <p className="text-sm text-green-700">
                  {t('pages.settings:sections.members.seatUpgrade.newSeats', {
                    count: calculateNewLimits(seatsToAdd).seats,
                  })}
                </p>
                <p className="text-sm text-green-700">
                  {t('pages.settings:sections.members.seatUpgrade.newMembers', {
                    count: calculateNewLimits(seatsToAdd).members,
                  })}
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                {error}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDialog(false);
                setError(null);
                setSeatsToAdd(1);
              }}
              disabled={isAdding}
            >
              {t('common:alerts.cancel')}
            </Button>
            <Button
              onClick={handleAddSeats}
              disabled={isAdding}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isAdding ? (
                <>
                  <LogoLoader size="sm" className="mr-2" />
                  {t('pages.settings:sections.members.seatUpgrade.adding')}
                </>
              ) : (
                t('pages.settings:sections.members.seatUpgrade.confirm')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SeatUpgradePrompt;
